import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { FormProvider } from 'react-hook-form';
import { apiUserService } from '@api';
import { EAddressType, TAddress, TBusinessAddress } from '@xeppt/xeppt-sdk/types/user';
import { useForm } from '@hooks/helpers/useForm';
import { defaultAddressData } from '@const/default_form_data';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { validator } from '@xeppt/xeppt-sdk';
import FullAddress from '@components/address';
import { useLocales } from '@hooks/helpers/useLocales';
import { Country } from 'country-state-city';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { Icon } from '@components/icons';

const addresses = {
    [EAddressType.TRADING]: 'trading_address',
    [EAddressType.REGISTRATION]: 'registration_address',
    [EAddressType.BUSINESS]: 'business_address'
};

interface IProps {
    data: TBusinessAddress;
}

const BusinessAddressSection: FC<IProps> = ({ data }) => {
    const [isHigh, setIsHigh] = useState(false);
    const {
        validationLocale,
        requestSuccessLocale,
        requestErrorLocale,
        labelLocale,
        submitLocale
    } = useLocales();
    const formMethods = useForm({
        defaultValues: {
            trading_address: defaultAddressData,
            registration_address: defaultAddressData,
            business_address: defaultAddressData
        },
        resolver: zodResolver(
            z.object({
                trading_address: validator.address({
                    address_1: validationLocale('address_1'),
                    address_2: validationLocale('address_2'),
                    zip_code: validationLocale('zip_code'),
                    city: validationLocale('city'),
                    country: validationLocale('country'),
                    region: validationLocale('region')
                }),
                registration_address: validator.address({
                    address_1: validationLocale('address_1'),
                    address_2: validationLocale('address_2'),
                    zip_code: validationLocale('zip_code'),
                    city: validationLocale('city'),
                    country: validationLocale('country'),
                    region: validationLocale('region')
                }),
                business_address: validator.address({
                    address_1: validationLocale('address_1'),
                    address_2: validationLocale('address_2'),
                    zip_code: validationLocale('zip_code'),
                    city: validationLocale('city'),
                    country: validationLocale('country'),
                    region: validationLocale('region')
                })
            })
        )
    });
    const [editableAddress, setEditableAddress] = useState<EAddressType>();

    const defaultData = useMemo(() => {
        return {
            trading_address: {
                //@ts-ignore
                ...data?.trading_address,
                country:
                    Country.getAllCountries().find(
                        //@ts-ignore
                        (item) => item.isoCode === data?.trading_address?.country
                        //@ts-ignore
                    )?.name || data?.trading_address?.country
            },
            registration_address: {
                //@ts-ignore
                ...data?.registration_address,
                country:
                    Country.getAllCountries().find(
                        //@ts-ignore
                        (item) => item.isoCode === data?.registration_address?.country
                        //@ts-ignore
                    )?.name || data?.registration_address?.country
            },
            business_address: {
                //@ts-ignore
                ...data?.business_address,
                country:
                    Country.getAllCountries().find(
                        //@ts-ignore
                        (item) => item.isoCode === data?.business_address?.country
                        //@ts-ignore
                    )?.name || data?.business_address?.country
            },
            billing_address: {
                //@ts-ignore
                ...data?.billing_address,
                country:
                    Country.getAllCountries().find(
                        //@ts-ignore
                        (item) => item.isoCode === data?.billing_address?.country
                        //@ts-ignore
                    )?.name || data?.billing_address?.country
            }
        };
    }, [data]);

    useEffect(() => {
        if (data) {
            formMethods.reset(defaultData);
        }
    }, [data]);

    const { handleRequest: onSubmit, isLoading } = useApiMutation({
        method: (type: Exclude<EAddressType, EAddressType.BILLING>) => {
            const values = formMethods.getValues();
            const name = addresses[type];
            //@ts-ignore
            const address: TAddress = values?.[name] as TAddress;
            return apiUserService.updateAddress(type, {
                ...address,
                country:
                    Country.getAllCountries().find((item) => item.name === address.country)
                        ?.isoCode || address.country
            });
        },
        onSuccess: () => {
            setEditableAddress(undefined);
            requestSuccessLocale('update_address');
        },
        onError: requestErrorLocale
    });

    const onCancel = (name: string) => {
        setIsHigh(true);
        if (data && name in data) {
            formMethods.reset({
                [name]: {
                    //@ts-ignore
                    ...data[name]
                }
            });
        }
        setEditableAddress(undefined);
        setTimeout(() => {
            setIsHigh(false);
        }, 0);
    };

    const dynamicTitle = (type: EAddressType) => {
        switch (type) {
            case EAddressType.BILLING:
                return 'billing_address';
            case EAddressType.REGISTRATION:
                return 'registration_address';
            case EAddressType.TRADING:
                return 'trading_address';
            case EAddressType.BUSINESS:
                return 'business_address';
        }
    };

    const dynamicValue = (name: string) => {
        //@ts-ignore
        const value: TAddress = formMethods.getValues()?.[name] as TAddress;
        if (value?.country && value?.zip_code && value?.address_1 && value?.region && value?.city) {
            return `${value.address_1},${value.address_2 ? ` ${value.address_2},` : ''} ${value.city}, ${value.region ? ` ${value.region},` : ''} ${value.zip_code}, ${value.country}`;
        }
        return '';
    };

    return (
        <div className={styles.container}>
            <FormProvider {...formMethods}>
                <div className={styles.header}>
                    <div className={styles.left}>
                        <div className={styles.icon}>
                            <Icon name="map_pointer" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <Typography
                                className={styles.title}
                                variant="body1"
                                lh="120%"
                                weight="semibold">
                                Company Addresses
                            </Typography>
                            <p className={styles.description}>
                                You can change your company addresses here
                            </p>
                        </div>
                    </div>
                </div>
                {Object.keys(addresses).map((key) => {
                    return (
                        <div key={key} className={styles.content}>
                            {!isHigh && editableAddress === Number(key) && (
                                <FullAddress
                                    name={
                                        addresses[
                                            Number(key) as Exclude<
                                                EAddressType,
                                                EAddressType.BILLING
                                            >
                                        ]
                                    }
                                    isFull={editableAddress === Number(key)}
                                    disabled={editableAddress !== Number(key)}
                                />
                            )}
                            {editableAddress !== Number(key) && (
                                <div className={styles.row}>
                                    <div className={`${styles.data_wrapper} ${styles.full}`}>
                                        <p className={styles.label}>
                                            {labelLocale(dynamicTitle(Number(key) as EAddressType))}
                                        </p>
                                        <div className={styles.address_value_wrapper}>
                                            <p className={styles.description}>
                                                {dynamicValue(
                                                    addresses[
                                                        Number(key) as Exclude<
                                                            EAddressType,
                                                            EAddressType.BILLING
                                                        >
                                                    ]
                                                )}
                                            </p>
                                            {editableAddress !== Number(key) && (
                                                <button
                                                    onClick={() =>
                                                        setEditableAddress(
                                                            Number(key) as EAddressType
                                                        )
                                                    }>
                                                    <Icon name="edit" width={18} height={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {editableAddress === Number(key) && (
                                <div className={styles.actions}>
                                    <Button
                                        onClick={onCancel}
                                        variant="outlined-primary"
                                        size="medium">
                                        {submitLocale('cancel')}
                                    </Button>
                                    <Button
                                        isLoading={isLoading}
                                        onClick={() =>
                                            onSubmit(
                                                Number(key) as Exclude<
                                                    EAddressType,
                                                    EAddressType.BILLING
                                                >
                                            )
                                        }
                                        variant="primary"
                                        size="medium">
                                        {submitLocale('save_changes')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </FormProvider>
        </div>
    );
};

export default BusinessAddressSection;
